<script setup lang="ts">
  /**
   * This component dynamically renders the appropriate ad component based on the provided ad type and placement from props in AdSettings and $Filters(user geo/data).
   * It can be used universally in the code base instead of calling direct ad provider components for easier A/B testing.
   */
  import {
    computed,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
    type Component,
  } from 'vue'
  import Outbrain from '../AdOps/Outbrain.vue'
  import ArticleCard, {
    type ArticleCardProps,
  } from '../ArticleCard/ArticleCard.vue'
  import Taboola from '../AdOps/Taboola.vue'
  import Assertive from '../AdOps/Assertive.vue'
  import GenericAPIAd from '../AdOps/GenericAPIAd.vue'
  import Primis from '../AdOps/Primis.vue'
  import { createExtrnId } from '../../utils/string/uuid'
  import { userEngagementStore } from '../../store/userData/UserEngagementStore'
  import { adConfigStore } from '../../store/ad-ops/adConfigStore'
  import type { AdProps, AdProviderType } from '../../ad_ops/providers/types'
  import { ayManagerFailed } from '../../utils/ad-ops/assertive-yield/queue'
  import {
    getFallbackPlacementWidget,
    resolveFallbackProvider,
  } from '../../utils/ad-ops/adFunctions'
  import { fallbackContainerName } from '../../composables/ad-ops/useTaboolaAds'
  import { withReadyAyManager } from '../../utils/consent/adReadiness'

  const providerMap: Record<string, Component> = {
    assertive: Assertive,
    outbrain: Outbrain,
    taboola: Taboola,
    dplxapi: GenericAPIAd, // creative-based provider uses GenericAPIAd
    primis: Primis,
  }

  export interface AdHandlerProps {
    ad?: boolean
    placement: string
    adType?: AdProviderType
    engagedAdType?: AdProviderType | null
    adHeight?: string
    adWidth?: string
    design?: DesignVariant
    variant?: ArticleCardProps['variant']
    tilePosition?: number
    disableStyleWrapper?: boolean
    lazyRender?: boolean
    sticky?: boolean
  }

  const props = withDefaults(defineProps<AdHandlerProps>(), {
    ad: true,
    adType: 'programmatic',
    engagedAdType: null,
    adHeight: 'auto',
    adWidth: 'auto',
    design: 'msn',
    variant: 'default',
    tilePosition: -1,
    disableStyleWrapper: false,
    lazyRender: false,
    sticky: false,
  })

  const { isDevMode, devDisableAds } = useRuntimeConfig().public

  const adSettingsStore = useAdSettingsStore()
  const { data: adSettings } = storeToRefs(adSettingsStore)
  const adConfigs = adConfigStore()
  const userEngagement = userEngagementStore()

  const effectiveVariant = computed(() =>
    adSettings.value.enableSubtleGreyAdBackground
      ? 'subtle-greybg'
      : props.variant
  )
  const style = computed(() =>
    getArticleStyleVariants({ ...props, variant: effectiveVariant.value })
  )
  // The native widgets (Taboola, Outbrain) inject their loaders when their
  // component is created, which a server render cannot do. On a server
  // rendered page they wait behind a box of the same size until hydration,
  // then the real widget mounts. A client rendered page renders them straight
  // away, which is every tenant today.
  const nativeWidgetsReady = ref(!isBeforeHydration())
  onMounted(() => {
    nativeWidgetsReady.value = true
  })

  const { client } = await useOpenFeature()

  // Minted after mount, never in server HTML. The id is how the ad stack tells
  // impressions apart, and a page cached for a group would hand the same id to
  // everyone in it. Empty on both sides for the first render, so hydration
  // matches, then the key below remounts the provider with the real id, which
  // is why nothing in the ad stack sees an empty one.
  const adInstanceId = ref<string>('')
  onMounted(() => {
    adInstanceId.value = createExtrnId()
  })
  const isEngagedUser = userEngagement.isEngaged

  // engaged user override for ad type
  const finalAdType: AdProviderType = isEngagedUser
    ? props.engagedAdType || props.adType
    : props.adType

  const isValidAdProviderType = (value: string): value is AdProviderType => {
    return ['outbrain', 'taboola', 'assertive', 'dplxapi', 'primis'].includes(
      value
    )
  }

  // determine the ad provider based on ad type and feature flags
  const requestedProvider = computed((): AdProviderType => {
    switch (finalAdType) {
      case 'programmatic':
        return 'assertive'
      case 'native': {
        const nativeProvider = client.getStringValue(
          'nativeAdProvider',
          'outbrain'
        )
        // Ensure the feature flag value is a valid AdProviderType
        return isValidAdProviderType(nativeProvider)
          ? nativeProvider
          : 'outbrain'
      }
      default:
        return finalAdType || 'assertive'
    }
  })

  // Without the AY manager a programmatic slot can never fill, so it takes the
  // placement's native fallback widget.
  const ayFallback = computed(() => {
    if (requestedProvider.value !== 'assertive' || !ayManagerFailed()) {
      return null
    }
    if (!client.getBooleanValue('nativeFallbackEnabled', true)) return null

    const nativeProvider = client.getStringValue('nativeAdProvider', 'outbrain')
    if (
      resolveFallbackProvider(nativeProvider, props.placement) === 'taboola'
    ) {
      const widget = getFallbackPlacementWidget(props.placement, 'taboola')
      if (!widget) return null
      return {
        provider: 'taboola' as const,
        config: {
          ...widget,
          containerName: fallbackContainerName(widget.containerName),
          placement: `${widget.placement}-fallback`,
        },
      }
    }

    const widget = getFallbackPlacementWidget(props.placement)
    if (!widget?.widgetId) return null
    return {
      provider: 'outbrain' as const,
      config: { ...widget, placement: `${widget.placement}-fallback` },
    }
  })

  const adProvider = computed(
    (): AdProviderType => ayFallback.value?.provider ?? requestedProvider.value
  )

  // get the specific ad config for the placement and provider
  const adProviderConfig = computed(() => {
    return (
      ayFallback.value?.config ??
      adConfigs.getConfigForPlacement(props.placement, adProvider.value)
    )
  })

  // Native widgets only wait for the consent read. The AY slot also waits for
  // a GDPR reader's decision, so its fallback does too.
  const fallbackMayRequest = ref(false)
  let cancelFallbackWait = () => {}
  watch(
    () => Boolean(ayFallback.value),
    (fallback) => {
      if (!fallback || fallbackMayRequest.value) return
      cancelFallbackWait = withReadyAyManager(
        (run) => run(),
        () => {
          fallbackMayRequest.value = true
        }
      )
    },
    { immediate: true }
  )
  onBeforeUnmount(() => cancelFallbackWait())

  // determine which component to render based on the ad provider
  const component = computed(() => {
    return providerMap?.[adProvider.value] || ArticleCard
  })

  const BROWSER_ONLY_PROVIDERS: ReadonlySet<string> = new Set([
    'taboola',
    'outbrain',
  ])
  const renderProviderComponent = computed(
    () =>
      (!BROWSER_ONLY_PROVIDERS.has(adProvider.value) ||
        nativeWidgetsReady.value) &&
      (!ayFallback.value || fallbackMayRequest.value)
  )

  const componentProps = computed(() => {
    const baseProps: AdProps = {
      ...props,
      ...(adProviderConfig.value || {}),
      adType: adProvider.value,
      class: props.disableStyleWrapper ? undefined : style.value.wrapper,
      style: style.value,
      adInstanceId: adInstanceId.value,
    }

    return {
      ...baseProps,
      variant: effectiveVariant.value,
    }
  })

  //style for the ad container
  const styles = computed(() => ({
    width: componentProps.value?.adWidth,
    height: componentProps.value?.adHeight,
    overflow: 'hidden',
    alignSelf: 'center',
    alignContent: 'center',
  }))

  const handleClick = () => {
    userEngagement.engage('ad')
  }
</script>

<template>
  <div
    v-if="devDisableAds && isDevMode"
    :key="componentProps.adInstanceId + '-dev'"
    v-bind="componentProps"
    aria-label="Partner Content"
    role="article"
    :style="styles"
    style="background-color: lightgray"
    @click="handleClick"
  >
    <code style="text-align: center">
      Placement: {{ props.placement }} | Provider: {{ adProvider }} | Ad Type:
      {{ props.adType }} | Size: {{ componentProps.adHeight }} x{{
        componentProps.adWidth
      }}
    </code>
  </div>
  <component
    :is="component"
    v-else-if="renderProviderComponent && componentProps.adInstanceId"
    :key="componentProps.adInstanceId"
    v-bind="componentProps"
    aria-label="Partner Content"
    role="article"
    :style="styles"
    :data-ay-fallback="ayFallback ? 'true' : undefined"
    @click="handleClick"
  />
  <div v-else :style="styles" data-testid="ad-placeholder" />
</template>
